import React, { useState } from 'react'
import { useAuthActions } from '@convex-dev/auth/react'

const LoginPage: React.FC = () => {
  const { signIn } = useAuthActions()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    // Validation
    if (!email.trim() || !password) {
      setError('Email and password are required')
      setIsLoading(false)
      return
    }

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }
    
    try {
      await signIn('password', { 
        email: email.toLowerCase().trim(), 
        password,
        flow: isSignUp ? 'signUp' : 'signIn'
      })
    } catch (error) {
      console.error('Authentication failed:', error)
      
      // Extract meaningful error message
      let errorMessage = 'Authentication failed. Please try again.'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message
      }
      
      // Check for common authentication errors
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.'
      } else if (errorMessage.includes('User already exists')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.'
      } else if (errorMessage.includes('User not found')) {
        errorMessage = 'No account found with this email. Please sign up first.'
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-page" data-testid="login-form">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Claims Administration System</h1>
            <p>Life and Disability Insurance Claims Management</p>
          </div>
          
          <div className="login-content">
            {error && (
              <div className="error-message" data-testid="auth-error-message" style={{
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                color: '#c33'
              }}>
                <strong>Authentication Error:</strong>
                <br />
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  disabled={isLoading}
                  data-testid="email-input"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    marginBottom: '16px'
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                  data-testid="password-input"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    marginBottom: '16px'
                  }}
                />
              </div>

              {isSignUp && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    disabled={isLoading}
                    data-testid="confirm-password-input"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                      marginBottom: '16px'
                    }}
                  />
                </div>
              )}

              <button 
                type="submit"
                data-testid="auth-submit-button"
                disabled={isLoading}
                className={`sign-in-button ${isLoading ? 'loading' : ''}`}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  marginBottom: '16px'
                }}
              >
                {isLoading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
              </button>
            </form>
            
            <div className="login-toggle">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp)
                  setError(null)
                  setPassword('')
                  setConfirmPassword('')
                }}
                disabled={isLoading}
                data-testid="toggle-auth-mode"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </button>
            </div>

            <div className="login-info">
              <p>
                {isSignUp 
                  ? 'Create a new account to access the claims administration system.'
                  : 'Please sign in to access the claims administration system.'
                }
              </p>
              {error && (
                <p style={{ color: '#666', fontSize: '0.9em', marginTop: '8px' }}>
                  If you continue to experience issues, please contact your system administrator.
                </p>
              )}
            </div>
            <div style={{display:'none'}} data-testid="session-expired-message">Session expired</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage